import { AngularLayout, FlexLayout } from '@rhtml/modifiers';
import { Component, css, html, LitElement } from '@rxdi/lit-html';
import { ReactivumTheme } from '@shared/styles/theme';

/**
 * @customElement frameworks-component
 */
@Component({
  selector: 'frameworks-component',
  styles: [
    css`
      ${ReactivumTheme}

      :host {
        display: block;
        color: var(--text-primary);
      }

      .libraries-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
        gap: 32px;
        margin-top: 40px;
      }

      .library-name {
        font-size: 1.75rem;
        font-weight: 700;
        margin-bottom: 12px;
        color: var(--primary-color);
        font-family: 'Courier New', monospace;
      }

      .library-tagline {
        font-size: 1rem;
        color: var(--text-secondary);
        margin-bottom: 16px;
        font-style: italic;
        font-weight: 400;
      }

      .library-description {
        font-size: 1rem;
        line-height: 1.6;
        color: var(--text-secondary);
        margin-bottom: 20px;
        font-weight: 400;
      }

      .github-link {
        display: inline-flex;
        align-items: center;
        margin-top: 16px;
        padding: 10px 20px;
        background: var(--bg-secondary);
        border: 1px solid var(--border-light);
        border-radius: var(--radius-md);
        color: var(--primary-color);
        text-decoration: none;
        font-size: 0.9rem;
        font-weight: 500;
        transition: all 0.3s ease;
      }

      .github-link:hover {
        background: var(--primary-color);
        color: white;
        border-color: var(--primary-color);
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(0, 102, 255, 0.3);
      }

      @media (max-width: 768px) {
        .libraries-grid {
          grid-template-columns: 1fr;
          gap: 24px;
        }
      }
    `,
  ],
  modifiers: [...FlexLayout, ...AngularLayout],
  template() {
    return html`
      <section class="section" id="frameworks">
        <div class="container">
          <h2 class="section-title">Our Complete Technology Stack</h2>

          <!-- Why Choose Us Section -->
          <div class="card card-bordered">
            <h3 class="card-title">Why Reactive Solutions?</h3>
            <div class="card-description">
              <p>
                We offer a unique ecosystem of in-house, open-source tools designed for both frontend and backend
                development, built using the latest technologies and standards. Our solutions empower developers to work
                more efficiently and consistently, providing significant value to companies looking to scale.
              </p>
            </div>
          </div>

          <!-- Key Advantages Grid -->
          <div class="libraries-grid" style="margin-top: 40px;">
            <div class="card library-card">
              <div class="library-name">Expert Team</div>
              <div class="library-tagline">Compact Development Team of Experts</div>
              <div class="library-description">
                Our team consists of highly skilled developers with extensive experience in modern web technologies. We
                maintain a compact yet efficient team structure to ensure direct communication and rapid development.
              </div>
              <ul class="library-features">
                <li>Highly skilled developers</li>
                <li>Extensive modern tech experience</li>
                <li>Compact, efficient team structure</li>
                <li>Direct communication</li>
                <li>Rapid development cycles</li>
              </ul>
            </div>

            <div class="card library-card">
              <div class="library-name">Modern Web Components</div>
              <div class="library-tagline">@rhtml Web Component Approach</div>
              <div class="library-description">
                We specialize in modern web component development using our @rhtml framework with GraphQL queries. Our
                approach provides better performance, smaller bundle sizes, and native browser compatibility.
              </div>
              <ul class="library-features">
                <li>@rhtml web component framework</li>
                <li>GraphQL query integration</li>
                <li>Native browser compatibility</li>
                <li>Smaller bundle sizes</li>
                <li>Better performance</li>
              </ul>
            </div>

            <div class="card library-card">
              <div class="library-name">Productivity Focus</div>
              <div class="library-tagline">We Love Being Productive</div>
              <div class="library-description">
                Efficiency drives us - our mission is turning ideas into high quality software with smart solutions. We
                utilize cutting-edge tools and methodologies to maximize productivity and deliver results quickly.
              </div>
              <ul class="library-features">
                <li>Efficiency-driven development</li>
                <li>High quality software delivery</li>
                <li>Smart solution design</li>
                <li>Cutting-edge tools</li>
                <li>Rapid results delivery</li>
              </ul>
            </div>
          </div>

          <!-- Frontend Frameworks Section -->
          <div class="card card-bordered" style="margin-top: 60px;">
            <h3 class="card-title">The Well-Known Frontend Frameworks</h3>
            <div class="card-description">
              <p>
                Our frontend approach is built around modern web components and reactive programming, providing a
                lightweight, performant alternative to traditional frameworks.
              </p>
            </div>
          </div>

          <div class="libraries-grid" style="margin-top: 40px;">
            <div class="card library-card">
              <div class="library-name">@rhtml</div>
              <div class="library-tagline">Modern Web Components</div>
              <div class="library-description">
                The world's smallest dependency injection system at less than 1.4kb. Features @Component decorator,
                declarative operators, and monadic programming patterns for ultra-fast web applications.
              </div>
              <ul class="library-features">
                <li>&lt;1.4kb DI system</li>
                <li>@Component decorator</li>
                <li>Declarative operators (r-let, r-if)</li>
                <li>Server Side Rendering ready</li>
                <li>Monadic programming patterns</li>
              </ul>
              <div class="library-stats">
                <div class="library-stat">
                  <span class="library-stat-number">1.4kb</span>
                  <span class="library-stat-label">Size</span>
                </div>
                <div class="library-stat">
                  <span class="library-stat-number">0.3kb</span>
                  <span class="library-stat-label">Bundled</span>
                </div>
              </div>
              <a href="https://github.com/r-html/rhtml" class="github-link" target="_blank">View on GitHub →</a>
            </div>

            <div class="card library-card">
              <div class="library-name">@rxdi</div>
              <div class="library-tagline">Reactive Extension Dependency Injection</div>
              <div class="library-description">
                A powerful dependency injection container that works seamlessly in both browser and Node.js
                environments. Built with reactive programming principles, it provides the foundation for all our
                frameworks.
              </div>
              <ul class="library-features">
                <li>Cross-platform DI container</li>
                <li>Reactive programming support</li>
                <li>Angular-inspired decorators</li>
                <li>TypeScript-first design</li>
              </ul>
              <div class="library-stats">
                <div class="library-stat">
                  <span class="library-stat-number">6+</span>
                  <span class="library-stat-label">Years</span>
                </div>
                <div class="library-stat">
                  <span class="library-stat-number">∞</span>
                  <span class="library-stat-label">Flexibility</span>
                </div>
              </div>
              <a href="https://github.com/rxdi/rxdi" class="github-link" target="_blank">View on GitHub →</a>
            </div>
          </div>

          <!-- Backend Solutions Section -->
          <div class="card card-bordered" style="margin-top: 60px;">
            <h3 class="card-title">The Modern Backend Solutions</h3>
            <div class="card-description">
              <p>
                Our backend solutions are built with GraphQL-first architecture, providing powerful APIs with minimal
                effort and maximum flexibility.
              </p>
            </div>
          </div>

          <div class="libraries-grid" style="margin-top: 40px;">
            <div class="card library-card">
              <div class="library-name">@gapi</div>
              <div class="library-tagline">GraphQL API Framework</div>
              <div class="library-description">
                Inspired by Angular, @gapi provides complex Node.js GraphQL backend applications with minimum effort.
                Features decorators, guards, interceptors, and comprehensive TypeScript support.
              </div>
              <ul class="library-features">
                <li>GraphQL-first architecture</li>
                <li>Decorator-based development</li>
                <li>Built-in authentication & guards</li>
                <li>Heroku & AWS Serverless ready</li>
                <li>27+ GitHub stars</li>
              </ul>
              <div class="library-stats">
                <div class="library-stat">
                  <span class="library-stat-number">27</span>
                  <span class="library-stat-label">Stars</span>
                </div>
                <div class="library-stat">
                  <span class="library-stat-number">308</span>
                  <span class="library-stat-label">Releases</span>
                </div>
              </div>
              <a href="https://github.com/Stradivario/gapi" class="github-link" target="_blank">View on GitHub →</a>
            </div>

            <div class="card library-card">
              <div class="library-name">graphql-server.com</div>
              <div class="library-tagline">Serverless Backend Platform</div>
              <div class="library-description">
                Our flagship platform combining Agile/Kanban project management with instant GraphQL/REST backend
                generation using custom serverless architecture on Kubernetes.
              </div>
              <ul class="library-features">
                <li>Instant backend generation</li>
                <li>Agile/Kanban project management</li>
                <li>Serverless architecture</li>
                <li>Kubernetes orchestration</li>
                <li>&lt;100ms response times</li>
              </ul>
              <div class="library-stats">
                <div class="library-stat">
                  <span class="library-stat-number">&lt;100ms</span>
                  <span class="library-stat-label">Response</span>
                </div>
                <div class="library-stat">
                  <span class="library-stat-number">∞</span>
                  <span class="library-stat-label">Scalability</span>
                </div>
              </div>
              <a href="https://graphql-server.com" class="github-link" target="_blank">Visit Platform →</a>
            </div>
          </div>

          <!-- DevOps Tooling Section -->
          <div class="card card-bordered" style="margin-top: 60px;">
            <h3 class="card-title">DevOps Tooling</h3>
            <div class="card-description">
              <p>
                Our DevOps approach ensures reliable, scalable deployments with modern containerization and
                orchestration technologies.
              </p>
            </div>
          </div>

          <div class="libraries-grid" style="margin-top: 40px;">
            <div class="card library-card">
              <div class="library-name">Kubernetes</div>
              <div class="library-tagline">Container Orchestration</div>
              <div class="library-description">
                We leverage Kubernetes for container orchestration, providing scalable, reliable deployments for our
                serverless architecture and microservices.
              </div>
              <ul class="library-features">
                <li>Container orchestration</li>
                <li>Auto-scaling capabilities</li>
                <li>High availability</li>
                <li>Service discovery</li>
                <li>Load balancing</li>
              </ul>
            </div>

            <div class="card library-card">
              <div class="library-name">Serverless</div>
              <div class="library-tagline">Cloud-Native Architecture</div>
              <div class="library-description">
                Our serverless approach provides cost-effective, scalable solutions with automatic resource management
                and pay-per-use pricing.
              </div>
              <ul class="library-features">
                <li>Pay-per-use pricing</li>
                <li>Automatic scaling</li>
                <li>Zero server management</li>
                <li>High availability</li>
                <li>Event-driven architecture</li>
              </ul>
            </div>
          </div>
        </div>
      </section>
    `;
  },
})
export class FrameworksComponent extends LitElement {}
